import type { DestinationIdentityPolicyDraft } from "../components/IdentityPolicyEditor";

export type WizardStep = "source" | "mode" | "transform" | "dest" | "params" | "policy" | "done";

export interface WizardState {
  step: WizardStep;
  sourceInstanceId: string | null;
  sourceConnectorType: string | null;
  mode: "webhook" | "poll" | "stream" | "sqs" | null;
  pollIntervalSec: number;
  transformId: string | null;
  destConnectorType: string | null;
  destInstanceId: string | null;
  params: Record<string, unknown>;
  /** Record adoption. Null is the default and today's behavior: every record the
   *  pipeline sends mints a new one at the destination. */
  identityPolicy: DestinationIdentityPolicyDraft | null;
  policy: "source-wins" | "dest-wins" | null;
  /** The pipeline this wizard already created. Set only when the create came
   *  back with warnings and kept the operator on the page, so the final step
   *  offers VIEW PIPELINE instead of a second CREATE. */
  createdId: string | null;
  /** Whether the create call already succeeded. Tracked separately from
   *  `createdId` because a create that returns warnings can come back WITHOUT a
   *  pipeline body, leaving `createdId` null — and a null id must still block a
   *  second create, or the operator gets a duplicate pipeline in their org. */
  created: boolean;
  /** Checks the API couldn't run on the pipeline it just created. */
  warnings: string[];
}

export const initialState: WizardState = {
  step: "source",
  sourceInstanceId: null,
  sourceConnectorType: null,
  mode: null,
  pollIntervalSec: 300,
  transformId: null,
  destConnectorType: null,
  destInstanceId: null,
  params: {},
  identityPolicy: null,
  policy: null,
  createdId: null,
  created: false,
  warnings: [],
};

export type WizardAction =
  | { type: "pickSource"; instanceId: string; connectorType: string }
  | { type: "pickMode"; mode: "webhook" | "poll" | "stream" | "sqs"; pollIntervalSec?: number }
  | { type: "pickTransform"; transformId: string; destConnectorType: string }
  | { type: "pickDest"; instanceId: string }
  | { type: "setParams"; params: Record<string, unknown> }
  | { type: "setIdentityPolicy"; identityPolicy: DestinationIdentityPolicyDraft | null }
  | { type: "confirmParams" }
  | { type: "pickPolicy"; policy: "source-wins" | "dest-wins" }
  | { type: "created"; createdId: string | null; warnings: string[] }
  | { type: "goto"; step: WizardStep };

export function wizardReducer(s: WizardState, a: WizardAction): WizardState {
  switch (a.type) {
    case "pickSource":
      return { ...s, step: "mode", sourceInstanceId: a.instanceId, sourceConnectorType: a.connectorType, mode: null };
    case "pickMode":
      return { ...s, step: "transform", mode: a.mode, pollIntervalSec: a.pollIntervalSec ?? s.pollIntervalSec };
    case "pickTransform":
      // A new transform means a new destination object type, so the adoption
      // policy's idField and write masks no longer describe anything real.
      return { ...s, step: "dest", transformId: a.transformId, destConnectorType: a.destConnectorType, destInstanceId: null, params: {}, identityPolicy: null };
    case "pickDest":
      return { ...s, step: "params", destInstanceId: a.instanceId };
    case "setParams":
      return { ...s, params: a.params };
    case "setIdentityPolicy":
      return { ...s, identityPolicy: a.identityPolicy };
    case "confirmParams":
      return { ...s, step: "policy" };
    case "pickPolicy":
      return { ...s, step: "done", policy: a.policy };
    case "created":
      return { ...s, created: true, createdId: a.createdId, warnings: a.warnings };
    case "goto":
      // Rewinding starts a fresh pipeline, so the last create's result goes with
      // it — otherwise a refilled wizard still points at the pipeline before it.
      return { ...s, step: a.step, created: false, createdId: null, warnings: [] };
  }
}
